/* eslint-disable max-lines */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { GetStaticPaths, InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
	BASE_FRAME_NAMES,
	DEFAULT_BASE_FRAME_SHAPE,
	DOUBLE_COLUMN_THRESHOLD,
	GROUP_BY_DEFAULT_ATTRIBUTE,
	TOP_FRAMES_PATH,
} from '@constants';
import {
	CollectionHero,
	CollectionSectionHeader,
	FilterRow,
	Flex,
	FrameShapeModalContent,
	HeroCarousel,
	Layout,
	Loading,
	Modal,
	Paragraph,
	PLPWrapper,
	ProductAndBlockGrid,
	ProductGrid,
	SEO,
} from '@components';
import { CONTENTFUL_TAG_LOGGEDOUT } from '@constants/contentful';
import { getGlobalProps } from '@utils/get-global-props';
import { getTopFramePLPStaticProps } from '@utils/get-top-frame-static-props';
import { useActiveFrame, useDebounce, useIntersectionObserver, useLocalStorage, usePreserveScroll } from '@utils/hooks';
import { capitalizeEachWord, findVisibleVariants, groupBy, useTranslation, withLocalizedPaths } from '@utils/index';
import { trackVariantListView } from '@services/analytics/trackers';
import { getPaginatedCollection } from '@services/shopify/operations';
import { BlockPLPFields, NormalizedVariant } from '@ts/index';
import { useCustomer } from '@services/shopify';
import { Customer } from '@ts/shopify-storefront-api';
import { getMarketingRedirect } from '@services/contentful';

type GroupedVariants = { [key: string]: Array<NormalizedVariant> };

const getTranslatedTexts = translator => {
	return {
		noTopsAvailableForFrame: translator('no-tops-available-for-frame'),
	};
};

const CollectionContent = ({
	variants,
	hasSections,
	blockPlpData,
	handle,
	productsCount,
	customer,
	addToCartCTA,
}: {
	variants: GroupedVariants;
	hasSections: boolean;
	blockPlpData: BlockPLPFields;
	handle: string;
	productsCount: number;
	customer: Customer;
	addToCartCTA?: boolean;
}) => {
	//--- Grid Rendering ---//
	const memoizedOutput = useMemo(() => {
		let variantRange = [0, 0];
		return Object.entries(variants).map(([key, variants], index) => {
			let heading = undefined;

			// define the "slice" of the full product list that this section lives in
			variantRange = [variantRange[1], variants.length + variantRange[1]];

			if (hasSections) {
				const variantWithGroupName = variants.find(variant => variant?.metafields?.groupName);
				if (key !== GROUP_BY_DEFAULT_ATTRIBUTE) heading = variantWithGroupName?.metafields?.groupName ?? ' ';
				if (key === GROUP_BY_DEFAULT_ATTRIBUTE)
					heading = variantWithGroupName?.metafields?.groupName ?? handle.replace('-', ' ');
			}

			const showHeader = !!heading;

			// if the PLP has sections, check and see which cards belong in the current section
			const safeCards = blockPlpData?.interactiveCards || [];
			const filteredCards = hasSections
				? safeCards.filter(card => {
						const loc = card.blockLocation - 1;
						return loc >= variantRange[0] && loc < variantRange[1];
					})
				: // we don't need to check the range if there are no sections
					safeCards;
			// before rendering, make sure that the locations of each card are relative to the length of the section they belong to
			const cardsToDisplay = filteredCards
				.map(card => {
					const tags = card?.metadata?.tags?.map(tag => tag.sys.id) ?? [];
					let show = true;
					if (!!customer && tags.includes(CONTENTFUL_TAG_LOGGEDOUT)) show = false;
					return { ...card, blockLocation: card.blockLocation - variantRange[0], show };
				})
				.filter(card => card.show);

			return (
				<Fragment key={`${key}-${index}`}>
					{blockPlpData?.productCarousel && (
						<HeroCarousel
							asPlp
							height={blockPlpData.productCarousel.height}
							slides={blockPlpData.productCarousel.slides}
							componentType={undefined}
							page={undefined}
						/>
					)}
					{!!showHeader && <CollectionSectionHeader heading={heading} />}
					<ProductGrid key={key} type={productsCount < DOUBLE_COLUMN_THRESHOLD ? 'tops-single' : 'tops'}>
						<ProductAndBlockGrid
							variants={variants}
							cards={cardsToDisplay}
							parentInfo={{
								parentCollectionHandle: handle,
								usingSingleColumnLayout: productsCount < DOUBLE_COLUMN_THRESHOLD,
							}}
							addToCartCTA={addToCartCTA}
						/>
					</ProductGrid>
				</Fragment>
			);
		});
	}, [blockPlpData?.interactiveCards, blockPlpData?.productCarousel, handle, hasSections, productsCount, variants, customer]);
	return memoizedOutput;
};

const Collection = ({ collection, blockPlpData, cursor }: InferGetStaticPropsType<typeof getStaticProps>) => {
	const { isFallback, query, locale } = useRouter();
	const Customer = useCustomer();
	const [, setHasSetDefaultFrame] = useLocalStorage<boolean>('hasSetDefaultFrame', false);
	const [activeFrame, setActiveFrame] = useActiveFrame();
	// The initial frame shape can be defined by the value in local storage, or by a query param
	const initialFrame =
		query?.frameShape && !Array.isArray(query?.frameShape)
			? (capitalizeEachWord(query.frameShape.toLowerCase()) as (typeof BASE_FRAME_NAMES)[number])
			: activeFrame;

	const [frameShape, setFrameShape] = useState<(typeof BASE_FRAME_NAMES)[number]>(initialFrame ?? DEFAULT_BASE_FRAME_SHAPE);
	const debouncedTrackVariantListView = useDebounce(trackVariantListView);
	const [bottomNode, setBottomNode] = useState<HTMLElement>();
	const [open, setOpen] = useState(false);
	const createNodeRef = useCallback((node: HTMLElement) => {
		if (node !== null) setBottomNode(node);
	}, []);
	const {
		data: paginatedData,
		isFetching: isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	} = useInfiniteQuery({
		queryKey: ['collection', collection?.handle, locale],
		queryFn: async ({ pageParam }) => {
			const data = getPaginatedCollection(collection.handle, pageParam, locale);
			if (!!data) {
				return data;
			}
		},
		getNextPageParam: lastPage => lastPage.cursor ?? undefined,
		getPreviousPageParam: firstPage => firstPage.cursor ?? undefined,
		...(!!cursor && { placeholderData: { pages: [{ page: collection, cursor: cursor }], pageParams: [cursor] } }),
		enabled: !!collection?.handle,
	});
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	const productData = paginatedData?.pages?.flatMap(page => page.page.products) ?? null;
	const { additionalInformation, useBigHero } = (blockPlpData ?? {}) as BlockPLPFields;
	const addInfo = additionalInformation
		? {
				...additionalInformation,
				additionalInformationTitle: additionalInformation?.additionalInformationTitle ?? `${collection.title} Collection`,
			}
		: null;
	const variantsMatchingFrameShape = findVisibleVariants({ ...collection, products: productData }, frameShape);
	const setIsPopulated = usePreserveScroll();
	const bottomOfContainer = useIntersectionObserver(bottomNode, {});

	const { groupTops: hasSections } = collection?.metafields ?? {};
	const variants: GroupedVariants = groupBy(variantsMatchingFrameShape, `${hasSections ? 'metafields.groupKey' : ''}`);
	const hasAvailableProducts = variantsMatchingFrameShape.length > 0 && !!collection?.handle;
	!!bottomOfContainer?.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage();

	const updateFrameShape = useCallback((frame: (typeof BASE_FRAME_NAMES)[number]) => {
		setFrameShape(frame);
	}, []);

	const seo = useMemo(
		() => ({
			title: collection?.seo?.title ?? `${collection?.title} - Pair Eyewear`,
			description: collection?.seo?.description ?? '',
		}),
		[collection]
	);

	useEffect(() => {
		setIsPopulated(true);
		debouncedTrackVariantListView({
			variants: variantsMatchingFrameShape,
			path: `${TOP_FRAMES_PATH}/${collection?.handle}/`,
		});
	}, [collection?.handle, debouncedTrackVariantListView, setIsPopulated, variantsMatchingFrameShape]);

	useEffect(() => {
		if (!Customer.isLoading) {
			const value = window.localStorage.getItem('hasSetDefaultFrame');
			const hasSetDefaultFrame = !!value && JSON.parse(value);
			setOpen(!hasSetDefaultFrame);
		}
	}, [Customer.isLoading]);

	useEffect(() => {
		if (!Customer.isLoading) {
			const value = window.localStorage.getItem('hasSetDefaultFrame');
			const hasSetDefaultFrame = !!value && JSON.parse(value);
			setOpen(!hasSetDefaultFrame);
		}
	}, [Customer?.isLoading, collection?.handle]);

	if (isFallback || !collection) {
		return (
			<Flex fullWidth style={{ height: '100vh' }} center position='relative'>
				<Loading removeDefaultStyling />
			</Flex>
		);
	}

	return (
		<>
			<Modal open={open} onOpenChange={() => setOpen(!open)}>
				<FrameShapeModalContent
					open={open}
					onOpenChange={() => setOpen(!open)}
					isPopupFrameSizing
					callback={newFrame => {
						newFrame && setActiveFrame(newFrame);
						newFrame && setFrameShape(newFrame);
						setHasSetDefaultFrame(true);
					}}
				/>
			</Modal>
			<SEO {...seo} />
			<CollectionHero collection={collection} ignoreCopy={!!additionalInformation?.plpAccordion} useBigHero={useBigHero} />
			<PLPWrapper {...addInfo}>
				<FilterRow withFrameShape updateFrameShape={updateFrameShape} buttonProps={{ label: frameShape }} />
				{!hasAvailableProducts && !isFetchingNextPage ? (
					<Flex justify='center' fullWidth>
						<Paragraph>{translations.noTopsAvailableForFrame}.</Paragraph>
					</Flex>
				) : (
					<CollectionContent
						variants={variants}
						hasSections={hasSections}
						blockPlpData={blockPlpData as BlockPLPFields}
						handle={collection?.handle}
						productsCount={variantsMatchingFrameShape.length}
						customer={Customer.data}
						addToCartCTA={!collection.metafields.shopNowButton}
					/>
				)}
				{isFetchingNextPage && (
					<Flex fullWidth style={{ height: '200px' }} center position='relative'>
						<Loading />
					</Flex>
				)}
			</PLPWrapper>
			<div ref={createNodeRef} style={{ height: '1px' }} />
		</>
	);
};

export const getStaticPaths: GetStaticPaths = withLocalizedPaths(async () => {
	return {
		paths: [],
		fallback: true,
	};
});

export const getStaticProps = async ({ params, locale }) => {
	try {
		const globalProps = await getGlobalProps({ locale });
		const { pageType: marketingPageType, redirectTo } = await getMarketingRedirect(`top-frames/${params.collection}`);

		if (marketingPageType === 'Redirect') {
			return {
				redirect: { destination: redirectTo, permanent: false },
			};
		}

		const { handle, ...pageProps } = await getTopFramePLPStaticProps(params.collection, locale);

		if (!pageProps.collection) throw new Error(`bad collection: ${handle}`);

		return {
			props: { ...globalProps, ...pageProps, background: 'gray0', locale },
			revalidate: 600,
		};
	} catch (error) {
		console.error(`SSG error in /top-frames/${params?.collection}: ${error}`);
		return { notFound: true, revalidate: 600 } as const;
	}
};

Collection.getLayout = function getLayout(page, pageProps) {
	return <Layout pageProps={pageProps}>{page}</Layout>;
};

export default Collection;
